/**
 * OutputPreview - Condensed output display for each pipeline stage
 * Shows appropriate preview based on stage type (text, images, video, etc.)
 */

import React from 'react';
import { 
  FileText, 
  Image, 
  Play, 
  Eye, 
  Download, 
  ExternalLink
} from 'lucide-react';
import { StageType } from '@/types/execution';
import { cn } from '@/utils/cn';

interface StageOutput {
  id: string;
  type: 'text' | 'json' | 'image' | 'video' | 'audio';
  content: any;
  preview: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  createdAt: string;
}

interface OutputPreviewProps {
  stage: StageType;
  outputs: StageOutput[];
  compact?: boolean;
  onViewFull?: (output: StageOutput) => void;
  className?: string;
}

const OutputPreview: React.FC<OutputPreviewProps> = ({
  stage,
  outputs,
  compact = false,
  onViewFull,
  className
}) => {
  const getStageIcon = (stageType: StageType) => {
    switch (stageType) {
      case 'script':
        return <FileText className="w-5 h-5" />;
      case 'vision':
        return <Eye className="w-5 h-5" />;
      case 'storyboard':
      case 'frames':
        return <Image className="w-5 h-5" />;
      case 'videos':
        return <Play className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const renderScriptPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="text-sm text-gray-600 line-clamp-3">
        {output.preview || "Script content preview..."}
      </div>
      {!compact && (
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <span>{output.content?.word_count || 0} words</span>
          <span>•</span>
          <span>{output.content?.estimated_duration || '0:00'} duration</span>
        </div>
      )}
    </div>
  );

  const renderVisionPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {(output.content?.themes || ['Visual Style', 'Color Palette', 'Mood']).slice(0, 3).map((theme: string, index: number) => (
          <span
            key={index}
            className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
          >
            {theme}
          </span>
        ))}
      </div>
      {!compact && (
        <div className="text-sm text-gray-600 line-clamp-2">
          {output.preview || "Visual style and tone guidelines..."}
        </div>
      )}
    </div>
  );

  const renderStoryboardPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1">
        {Array.from({ length: Math.min(4, output.content?.shots?.length || 4) }).map((_, index) => (
          <div
            key={index}
            className="aspect-video bg-gray-200 rounded flex items-center justify-center"
          >
            <Image className="w-4 h-4 text-gray-400" />
          </div>
        ))}
      </div>
      {!compact && (
        <div className="text-xs text-gray-500">
          {output.content?.shots?.length || 0} shots planned
        </div>
      )}
    </div>
  );

  const renderShotsPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="text-sm text-gray-600">
        <div className="font-medium">{output.content?.total_shots || 0} shots</div>
        <div className="text-xs text-gray-500">
          {output.content?.total_duration || '0:00'} total duration
        </div>
      </div>
      {!compact && output.content?.shot_breakdown && (
        <div className="space-y-1">
          {Object.entries(output.content.shot_breakdown).slice(0, 2).map(([type, count]) => (
            <div key={type} className="flex justify-between text-xs text-gray-500">
              <span className="capitalize">{type}</span>
              <span>{count as number}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContinuityPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="text-sm text-gray-600">
        <div className="font-medium">Continuity Elements</div>
        <div className="text-xs text-gray-500">
          {output.content?.elements_count || 0} elements tracked
        </div>
      </div>
      {!compact && output.content?.key_elements && (
        <div className="flex flex-wrap gap-1">
          {output.content.key_elements.slice(0, 3).map((element: string, index: number) => (
            <span
              key={index}
              className="inline-flex px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
            >
              {element}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const renderFramesPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1">
        {Array.from({ length: Math.min(3, output.content?.frames?.length || 3) }).map((_, index) => (
          <div
            key={index}
            className="aspect-video bg-gradient-to-br from-blue-200 to-purple-200 rounded flex items-center justify-center"
          >
            <Image className="w-4 h-4 text-white" />
          </div>
        ))}
      </div>
      {!compact && (
        <div className="text-xs text-gray-500">
          {output.content?.frames?.length || 0} keyframes generated
        </div>
      )}
    </div>
  );

  const renderVideoPreview = (output: StageOutput) => (
    <div className="space-y-2">
      <div className="aspect-video bg-black rounded-lg flex items-center justify-center relative">
        <Play className="w-8 h-8 text-white opacity-80" />
        {output.thumbnailUrl && (
          <img
            src={output.thumbnailUrl}
            alt="Video preview"
            className="absolute inset-0 w-full h-full object-cover rounded-lg"
          />
        )}
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-1 rounded">
          {output.content?.duration || '0:00'}
        </div>
      </div>
      {!compact && (
        <div className="text-xs text-gray-500">
          {output.content?.resolution || '1080p'} • {output.content?.format || 'MP4'}
        </div>
      )}
    </div>
  );

  const renderOutputPreview = (output: StageOutput) => {
    switch (stage) {
      case 'script':
        return renderScriptPreview(output);
      case 'vision':
        return renderVisionPreview(output);
      case 'storyboard':
        return renderStoryboardPreview(output);
      case 'shots':
        return renderShotsPreview(output);
      case 'continuity':
        return renderContinuityPreview(output);
      case 'frames':
        return renderFramesPreview(output);
      case 'videos':
        return renderVideoPreview(output);
      default:
        return <div className="text-sm text-gray-500">Output preview not available</div>;
    }
  };

  // Mock output data if none provided
  const mockOutput: StageOutput = {
    id: `${stage}-output`,
    type: stage === 'videos' ? 'video' : stage === 'frames' || stage === 'storyboard' ? 'image' : 'text',
    content: {},
    preview: `${stage} output preview...`,
    createdAt: new Date().toISOString(),
  };

  const displayOutputs = outputs.length > 0 ? outputs : [mockOutput];

  if (compact) {
    return (
      <div className={cn('space-y-2', className)}>
        {displayOutputs.slice(0, 1).map((output) => (
          <div key={output.id}>
            {renderOutputPreview(output)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {getStageIcon(stage)}
          <h4 className="font-medium text-gray-900 capitalize">{stage} Outputs</h4>
        </div>
        {displayOutputs.length > 1 && (
          <span className="text-xs text-gray-500">{displayOutputs.length} items</span>
        )}
      </div>

      <div className="space-y-4">
        {displayOutputs.map((output) => (
          <div
            key={output.id}
            className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
          >
            <div className="space-y-3">
              {renderOutputPreview(output)}
              
              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {new Date(output.createdAt).toLocaleString()}
                </div>
                <div className="flex items-center space-x-2">
                  {output.fileUrl && (
                    <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => onViewFull?.(output)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OutputPreview;